import { calculatePackPricing } from './packPricing';

const scopeValue = value => String(value ?? '').trim().toLowerCase();
const wildcard = value => ['', '*', 'all', '0'].includes(scopeValue(value));
const active = row => !['false', '0'].includes(scopeValue(row.is_active));
const matches = (value, actual) => wildcard(value) || (!wildcard(actual) && scopeValue(value) === scopeValue(actual));
const dateOnly = value => String(value || '').slice(0, 10);

export const explainPricingMismatch = (rows, context) => {
  if (!rows.length) return 'No margin configurations were loaded. Refresh Current Prices and check the configuration load error.';
  const fields = ['product_barcode_id', 'product_id', 'destination_type', 'dispatch_to_id', 'warehouse_id'];
  const candidates = rows.filter(row => matches(row.product_barcode_id, context.product_barcode_id));
  if (!candidates.length) return `No configuration covers product pack ${context.product_barcode_id}, and no universal product rule exists.`;
  return candidates.map(row => {
    const reasons = fields.filter(field => !matches(row[field], context[field]))
      .map(field => `${field}: rule ${row[field]}, dispatch ${context[field] ?? '(blank)'}`);
    if (!active(row)) reasons.unshift('inactive');
    return { id: row.id, reasons };
  }).sort((a, b) => a.reasons.length - b.reasons.length).slice(0, 3)
    .map(row => `Rule #${row.id}: ${row.reasons.join('; ') || 'scope matches'}`).join('. ');
};

export const resolvePricingConfig = (rows, context, market = false) => rows
  .filter(row => active(row)
    && matches(row.product_barcode_id, context.product_barcode_id)
    && matches(row.product_id, context.product_id)
    && matches(row.destination_type, context.destination_type)
    && matches(row.dispatch_to_id, context.dispatch_to_id)
    && matches(row.warehouse_id, context.warehouse_id)
    && (!market || dateOnly(row.effective_from) <= (context.date || new Date().toLocaleDateString('en-CA'))))
  .sort((a, b) => {
    if (!market && scopeValue(context.destination_type) === 'online') {
      const onlinePriority = Number(scopeValue(b.destination_type) === 'online') - Number(scopeValue(a.destination_type) === 'online');
      if (onlinePriority) return onlinePriority;
    }
    const specificity = row => ['product_barcode_id', 'product_id', 'destination_type', 'dispatch_to_id', 'warehouse_id']
      .filter(field => !wildcard(row[field])).length;
    const score = row => (!wildcard(row.product_barcode_id) ? 32 : 0) + (!wildcard(row.product_id) ? 16 : 0)
      + (!wildcard(row.destination_type) ? 8 : 0) + (!wildcard(row.dispatch_to_id) ? 4 : 0) + (!wildcard(row.warehouse_id) ? 2 : 0);
    return specificity(b) - specificity(a) || score(b) - score(a)
      || dateOnly(b.effective_from).localeCompare(dateOnly(a.effective_from)) || Number(b.id) - Number(a.id);
  })[0];

export const weightInGrams = (quantity, unit) => {
  const code = String(unit || '').toLowerCase().replace(/\s|\./g, '');
  if (code.startsWith('kg')) return Number(quantity) * 1000;
  if (code.startsWith('gm') || code === 'g' || code.startsWith('gram')) return Number(quantity);
  return 0;
};

export const proportionalPackMrp = (sourceMrp, sourceSize, packSize) => {
  if (![sourceMrp, sourceSize, packSize].every(v => Number.isFinite(Number(v)) && Number(v) > 0)) return null;
  const raw = Number(sourceMrp) * Number(packSize) / Number(sourceSize);
  return Math.ceil(raw - Number.EPSILON * Math.max(1, raw) * 4);
};

export const calculateStagePrice = ({ grams, purchaseCost, marketRates = [], ledgers = [], context, repackingCost = 0, fixedMrp }) => {
  const stockBased = ['outlet', 'internal_packing', 'online'].includes(scopeValue(context.destination_type));
  if (String(context.destination_type || '').trim().toLowerCase() === 'internal_packing' && !(Number(fixedMrp) > 0)) return null;
  const plan = resolvePricingConfig(ledgers, context);
  const defaultVendorMargin = String(context.destination_type || '').toLowerCase() === 'vendor'
    && plan && wildcard(plan.dispatch_to_id) && Number(plan.discount_percent || 0) === 0;
  const market = !stockBased && !defaultVendorMargin && grams > 0 ? resolvePricingConfig(marketRates, context, true) : null;
  const base = market ? Number(market.market_rate_per_kg) * grams / 1000 : Number(purchaseCost);
  if (!Number.isFinite(base) || base < 0 || !plan) return null;
  const cost = base + Number(repackingCost || 0);
  // A default zero discount is not a vendor-specific agreement to sell at MRP.
  if (defaultVendorMargin) {
    const margin = Number(plan.margin_percent || 0) * Number(plan.proportion_factor ?? 1);
    const raw = cost * (1 + margin / 100);
    if (!(cost > 0) || !Number.isFinite(raw) || raw <= 0) return null;
    const uncappedPrice = Math.ceil(raw - Number.EPSILON * Math.max(1, raw) * 4);
    const mrp = Number(fixedMrp) > 0 ? Math.ceil(Number(fixedMrp)) : uncappedPrice;
    const price = Math.min(uncappedPrice, mrp);
    return { price, mrp, uncappedPrice, cappedAtMrp: uncappedPrice > mrp,
      pricing_basis: 'purchase', market_rate_per_kg: null, market_config_id: null,
      ledger_config_id: plan.id, margin_percent: Number(plan.margin_percent || 0),
      proportion_factor: Number(plan.proportion_factor ?? 1), discount_percent: 0,
      effective_discount_percent: (mrp - price) / mrp * 100 };
  }
  // Stock labels carry a fixed MRP. A stakeholder's configured discount changes
  // the selling price, never that printed MRP. Keep whole-rupee price rounding.
  const stakeholder = ['vendor', 'customer'].includes(scopeValue(context.destination_type)) && !wildcard(context.dispatch_to_id);
  if (stakeholder && Number(fixedMrp) > 0 && plan.discount_percent != null) {
    const discount = Number(plan.discount_percent);
    if (!Number.isFinite(discount) || discount < 0 || discount >= 100) return null;
    const price = Math.min(Number(fixedMrp), Math.ceil(Number(fixedMrp) * (1 - discount / 100)));
    return { price, mrp: Number(fixedMrp), pricing_basis: 'stakeholder_discount',
      market_rate_per_kg: null, market_config_id: null, ledger_config_id: plan.id,
      margin_percent: Number(plan.margin_percent || 0), proportion_factor: Number(plan.proportion_factor ?? 1),
      discount_percent: discount };
  }
  const pricing = calculatePackPricing({
    unitCost: cost,
    marginPercent: plan.margin_percent,
    proportionFactor: plan.proportion_factor ?? 1,
    discountPercent: plan.discount_percent,
  });
  if (!(pricing.dprice > 0)) return null;
  const mrp = Number(fixedMrp) > 0 ? (stockBased ? Math.ceil(Number(fixedMrp)) : Number(fixedMrp)) : pricing.mrp;
  const cappedAtMrp = mrp > 0 && pricing.dprice > mrp;
  return { price: cappedAtMrp ? mrp : pricing.dprice, mrp, cappedAtMrp, uncappedPrice: pricing.dprice, pricing_basis: market ? 'market' : 'purchase',
    market_rate_per_kg: market ? Number(market.market_rate_per_kg) : null, market_config_id: market?.id ?? null,
    ledger_config_id: plan.id, margin_percent: Number(plan.margin_percent || 0),
    proportion_factor: Number(plan.proportion_factor ?? 1),
    discount_percent: Number(plan.discount_percent || 0),
    effective_discount_percent: mrp > 0 ? Math.max(0, (mrp - (cappedAtMrp ? mrp : pricing.dprice)) / mrp * 100) : 0 };
};

export const calculateStageBudgetGrams = ({ budget, costPerGram, ...parameters }) => {
  const stockBased = ['outlet', 'internal_packing', 'online'].includes(scopeValue(parameters.context.destination_type));
  const market = stockBased ? null : resolvePricingConfig(parameters.marketRates || [], parameters.context, true);
  const basisPerGram = market ? Number(market.market_rate_per_kg) / 1000 : costPerGram;
  if (!(basisPerGram > 0)) return 0;
  let low = 0;
  let high = Math.floor(budget / basisPerGram);
  while (low < high) {
    const mid = Math.ceil((low + high) / 2);
    const quote = calculateStagePrice({ ...parameters, grams: mid, purchaseCost: mid * costPerGram,
      fixedMrp: parameters.sourceMrp ? proportionalPackMrp(parameters.sourceMrp, parameters.sourceGrams, mid) : parameters.fixedMrp });
    if (!quote) return 0;
    if (quote.price <= budget) low = mid;
    else high = mid - 1;
  }
  return low;
};

export const priceDispatchLabelOrder = (order, configuration) => {
  const [type, destinationId] = String(order.destination || '').split(':');
  const warehouseId = order.source_warehouse_id || String(order.source || '').split(':')[1];
  const date = configuration.date || new Date().toLocaleDateString('en-CA', { timeZone:'Asia/Kolkata' });
  const errors = [];
  const items = (order.items || []).filter(item => item.id).map(item => {
    const configs = item.packing_configurations || item.packing_configs || [];
    if (type === 'internal_packing' && !configs.length) {
      errors.push(`Add packing sizes for item ${item.id} before printing current-price labels`);
      return item;
    }
    const context = { product_id:item.product_id, product_barcode_id:item.product_barcode_id,
      destination_type:type, dispatch_to_id:/^\d+$/.test(destinationId || '') ? destinationId : null,
      warehouse_id:warehouseId, date };
    const sourceCost = Number(item.landed_purchase_unit_price ?? item.inventory_unit_price ?? item.unit_price ?? 0);
    const sourceGrams = weightInGrams(item.barcode_quantity, item.unit_short_code || item.unit_name);
    const count = configs.reduce((sum,row) => sum + Number(row.pack_count || row.qty || 0), 0);
    const calculate = (config = null) => {
      const grams = config ? weightInGrams(config.barcode_quantity || config.quantity, config.unit_short_code || config.unit_name) : sourceGrams;
      const configuredSourceGrams = config ? weightInGrams(config.source_pack_quantity, config.source_pack_unit_short_code) : 0;
      const sourceWeight = configuredSourceGrams || sourceGrams;
      const purchaseCost = config ? (sourceWeight > 0 ? sourceCost * grams / sourceWeight : Number(config.package_amount || config.final_unit_cost)) : sourceCost;
      const prices = calculateStagePrice({ ...configuration, grams, purchaseCost,
        fixedMrp: !config ? item.inventory_unit_mrp || item.unit_mrp : proportionalPackMrp(item.inventory_unit_mrp || item.unit_mrp, sourceWeight, grams),
        repackingCost: config && count > 0 ? Number(config.repacking_cost_total || 0) / count : 0,
        context:{...context, product_barcode_id:config ? config.product_barcode_id || null : item.product_barcode_id} });
      if (!prices) {
        errors.push(`No applicable margin or cost for ${config?.product_name || item.product_name_eng || item.id}`);
        return config || item;
      }
      return { ...(config || item), dprice:prices.price, discounted_price:prices.price, selling_price:prices.price,
        mrp_amount:prices.mrp, unit_mrp:prices.mrp, pricing_basis:prices.pricing_basis,
        market_rate_per_kg:prices.market_rate_per_kg, margin_percent:prices.margin_percent,
        discount_percent:prices.effective_discount_percent,
        price_calculated_at:new Date().toISOString() };
    };
    if (!configs.length) return calculate();
    const priced = configs.map(config => calculate(config));
    return {...item, packing_configs:priced, packing_configurations:priced};
  });
  if (errors.length) { const error = new Error(errors.join('; ')); error.statusCode = 422; throw error; }
  return {...order,items,price_calculated_at:new Date().toISOString()};
};
